/**
 * Hook for fetching institutional page data by SLUG with localization
 * 
 * Use this hook when you only have a slug (e.g., "terms-of-use", "privacy-policy")
 * For ID-based lookups, use useInstitutionalPageLocalization instead
 */

import { useQuery } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useLocale } from '@/contexts/LocaleContext';
import { usePrimaryLocale } from '@/hooks/usePrimaryLocale';
import type { LocalizedInstitutionalPage } from './useInstitutionalPageLocalization';

interface InstitutionalPage {
  id: string;
  title: string;
  slug: string;
  content_html: string;
  excerpt: string | null;
  featured_image_url: string | null;
  location_id: number | null;
  source_language: string;
  published_at: string | null;
  view_count: number;
  no_index: boolean | null;
}

interface LocalizationMetadata {
  source_id: string;
  locale: string;
  translated_name: string | null;  // EN SOT: renamed from translated_primary
  translated_body_short: string | null;
  translated_body: string | null;
  translated_slug: string | null;
  meta_title: string | null;
  meta_description: string | null;
  // og_title, og_image_alt REMOVED - now derived at runtime via computeSEOFields()
  aio_snippet: string | null;
  no_index: boolean | null;
  schema_about: string | null;
}

interface UseInstitutionalPageBySlugResult {
  page: LocalizedInstitutionalPage | null;
  isLoading: boolean;
  isError: boolean;
  notFound: boolean;
}

export function useInstitutionalPageBySlug(slug: string): UseInstitutionalPageBySlugResult {
  const { settings } = useLocale();
  const { resolveToPrimary, isLoading: isLocaleLoading } = usePrimaryLocale();
  const primaryLocale = resolveToPrimary(settings.language);
  
  const { data, isLoading, isError } = useQuery({
    queryKey: ['institutional-page-by-slug', slug, primaryLocale],
    queryFn: async (): Promise<LocalizedInstitutionalPage | null> => {
      if (!slug) return null;
      
      // Step 1: Resolve slug to page (using English source slug)
      const basePage = await db.fetchOne<InstitutionalPage>(
        'content_institutional_pages',
        'id, title, slug, content_html, excerpt, featured_image_url, location_id, source_language, published_at, view_count, no_index',
        [
          { column: 'slug', operator: 'eq', value: slug },
          { column: 'editorial_status', operator: 'eq', value: 'published' },
        ]
      );
      
      if (!basePage) return null;
      
      // Step 2: Fetch localization_metadata for this page + locale
      // og_description REMOVED - now derived at runtime from meta_description
      // og_title, og_image_alt REMOVED - now derived at runtime via computeSEOFields()
      const localization = await db.fetchOne<LocalizationMetadata>(
        'localization_metadata',
        'source_id, locale, translated_name, translated_body_short, translated_body, translated_slug, meta_title, meta_description, aio_snippet, no_index, schema_about',
        [
          { column: 'source_type', operator: 'eq', value: 'page' },
          { column: 'source_id', operator: 'eq', value: basePage.id },
          { column: 'locale', operator: 'eq', value: primaryLocale },
        ]
      );
      
      // Step 3: STRICT LOCALE MATCHING - No fallback to English content
      const isNonEnglish = primaryLocale !== 'en';
      const isLocalized = !!localization && isNonEnglish;
      
      return {
        // Base page data
        id: basePage.id,
        slug: basePage.slug,
        content_html: isNonEnglish 
          ? (localization?.translated_body ?? basePage.content_html) 
          : basePage.content_html,
        featured_image_url: basePage.featured_image_url,
        published_at: basePage.published_at,
        view_count: basePage.view_count ?? 0,
        source_language: basePage.source_language,
        
        // Localized display fields - EN SOT: translated_name replaces translated_primary
        title: isNonEnglish 
          ? (localization?.translated_name ?? basePage.title) 
          : basePage.title,
        excerpt: isNonEnglish 
          ? (localization?.translated_body_short ?? basePage.excerpt) 
          : basePage.excerpt,
        
        // SEO fields
        meta_title: isNonEnglish 
          ? (localization?.meta_title ?? basePage.title) 
          : basePage.title,
        meta_description: isNonEnglish 
          ? (localization?.meta_description ?? basePage.excerpt ?? '') 
          : (basePage.excerpt || ''),
        // og_title, og_image_alt REMOVED - now derived at runtime via computeSEOFields()
        no_index: localization?.no_index ?? basePage.no_index ?? false,
        schema_about: localization?.schema_about || null,
        aio_snippet: localization?.aio_snippet || null,
        
        // Metadata
        isLocalized,
        currentLocale: primaryLocale,
      };
    },
    enabled: !!slug && !isLocaleLoading,
    staleTime: 5 * 60 * 1000,
  });
  
  return {
    page: data ?? null,
    isLoading: isLoading || isLocaleLoading,
    isError,
    notFound: !isLoading && !isLocaleLoading && !data,
  };
}
